import { PRICE_TAG_CROP, cropVideoToViewfinder } from "../barcodeDetect";
import { detectCodesInCanvas } from "./detectCodes";
import { recognizePriceTagText } from "./ocr";
import { parsePriceTag } from "./parse";
import { isUsableParsedTag, type ParsedPriceTag } from "./types";
import { debugError, type DebugStage, type PriceTagDebugObserver } from "./debug";

export class PriceTagRecognizeError extends Error {
  constructor(public readonly code: "NO_FRAME" | "NO_TEXT_DETECTED") {
    super(code);
    this.name = "PriceTagRecognizeError";
  }
}

export async function recognizePriceTagFromVideo(
  video: HTMLVideoElement,
  storeId?: string,
  debug?: PriceTagDebugObserver,
): Promise<ParsedPriceTag> {
  let stage: DebugStage = "capture";
  let started = performance.now();
  const start = (next: DebugStage) => {
    stage = next;
    started = performance.now();
    debug?.event({ stage, status: "started", elapsedMs: 0 });
  };
  const success = (data: unknown) => debug?.event({ stage, status: "success", elapsedMs: performance.now() - started, data });
  const failure = (error: unknown) => debug?.event({ stage, status: "error", elapsedMs: performance.now() - started, error: debugError(error) });
  try {
    start("capture");
    const canvas = cropVideoToViewfinder(video, document.createElement("canvas"), PRICE_TAG_CROP);
    if (canvas.width < 8 || canvas.height < 8) throw new PriceTagRecognizeError("NO_FRAME");
    success({
      video: { width: video.videoWidth, height: video.videoHeight },
      crop: { width: canvas.width, height: canvas.height, ...PRICE_TAG_CROP },
      display: { width: video.clientWidth, height: video.clientHeight },
    });
    if (debug) {
      try { debug.frame(canvas.toDataURL("image/jpeg", 0.85)); }
      catch (error) { failure(error); } // Preview failure must not prevent OCR.
    }

    start("codes");
    const codes = await detectCodesInCanvas(canvas);
    success(codes);
    start("ocr");
    let rawText = "";
    try {
      rawText = await recognizePriceTagText(canvas, debug ? success : undefined);
    } catch (error) {
      failure(error);
    }

    start("parse");
    const tag = parsePriceTag(rawText, {
      storeId,
      detectedBarcode: codes.barcode,
      detectedInternalCode: codes.internalCode,
    });
    const usable = isUsableParsedTag(tag);
    success({ tag, usable });

    if (!usable) throw new PriceTagRecognizeError("NO_TEXT_DETECTED");
    return tag;
  } catch (error) {
    failure(error);
    throw error;
  }
}
